import React, { useMemo, useState } from "react";

import { useGetFarmResilienceScore, useTranslation } from "utils";

import { ErrorBoundary, Flex, Icon, IconButton, Link, Tag, Tooltip, Trans, Typography } from "components";

import ResilienceScoreContextDrawer from "pages/app/resilience-score/components/resilience-score-context-drawer/ResilienceScoreContextDrawer";
import { buildResilienceScoreViewModel } from "pages/app/resilience-score/utils/ResilienceScoreUtils";

type RiskLevel = "low" | "medium" | "high";

type ResilienceScoreCardProps = {
  farmId: string | null;
  selectedYear: number;
};

const riskBadgeConfig: Record<RiskLevel | "awaitingData", { key: string; fallback: string; icon: string }> = {
  low: { key: "impactPanel.resilience.risk.low", fallback: "Low risk", icon: "tabler/arrow-down-right-circle" },
  medium: { key: "impactPanel.resilience.risk.medium", fallback: "Medium risk", icon: "tabler/arrow-right-circle" },
  high: { key: "impactPanel.resilience.risk.high", fallback: "High risk", icon: "tabler/arrow-up-right-circle" },
  awaitingData: { key: "impactPanel.resilience.awaitingData", fallback: "Awaiting data", icon: "tabler/loader" },
};

const TREND_ICON: Record<"up" | "down" | "flat" | "none", string> = {
  up: "tabler/arrow-up-right-circle",
  down: "tabler/arrow-down-right-circle",
  flat: "tabler/arrow-straight-right-circle",
  none: "tabler/arrow-straight-right-circle",
};

/** Format to 1 decimal place, truncating (no rounding). */
const formatScore = (value: number) => {
  if (value == null || Number.isNaN(value)) return "0";
  const n = Number(value);
  const truncated = Math.floor(n * 10) / 10;
  return truncated.toFixed(1) === "0.0" ? "0" : truncated.toFixed(1);
};

const formatChangePct = (pct: number | null): string => {
  if (pct == null || Number.isNaN(pct)) return "0";
  const sign = pct >= 0 ? "+" : "";
  return `${sign}${pct.toFixed(1)}`;
};

const ResilienceScoreCard: React.FC<ResilienceScoreCardProps> = ({ selectedYear, farmId }) => {
  const { t } = useTranslation(["resilienceScore"]);

  const { data: farmResilienceScore, isLoading: isResilienceLoading } = useGetFarmResilienceScore({
    farmId,
  });
  const resilienceScoreViewModel = useMemo(() => buildResilienceScoreViewModel(farmResilienceScore, selectedYear), [farmResilienceScore, selectedYear]);
  const [showContextDrawer, setShowContextDrawer] = useState(false);

  const hasScore = !!resilienceScoreViewModel && !Number.isNaN(resilienceScoreViewModel.overallScore);
  const isDataReady = !isResilienceLoading && hasScore;

  const overallScore = isDataReady ? resilienceScoreViewModel!.overallScore : 0;
  const maxScore = isDataReady ? resilienceScoreViewModel!.maxScore : 10;
  const riskLevel: RiskLevel | "awaitingData" = isDataReady ? resilienceScoreViewModel!.riskLevel : "awaitingData";
  const riskConfig = riskBadgeConfig[riskLevel];
  const TooltipLearnMoreLink = (
    <Link type="primary-link" disabled={!isDataReady} className="tooltip-learn-more-link" onClick={() => setShowContextDrawer(true)} />
  );

  return (
    <div className="resilience-score-card" data-testid="resilience-score-card">
      <Flex justify="space-between" align="center" className="resilience-header">
        <Flex align="center" gap="0.25rem">
          <Typography variant="paragraph-m" bold>
            {t("resilienceScoreCard.title", "Farm Resilience")}
          </Typography>
          <Tooltip placement="top" title={<Trans i18nKey="resilienceScore:resilienceScoreCard.tooltip" components={[TooltipLearnMoreLink]} />}>
            <Icon iconName="tabler/info-circle" size="s" className="resilience-info-icon" />
          </Tooltip>
        </Flex>

        <IconButton
          iconName="tabler/chevron-right"
          disabled={!isDataReady}
          size="small"
          className="resilience-drawer-icon pointer"
          onClick={() => setShowContextDrawer(true)}
          dataTestId="resilience-drawer-icon"
        />
      </Flex>

      <Flex justify="space-between" align="center" className="resilience-main-score-row">
        <Flex align="baseline" gap="0.25rem" className="resilience-main-score">
          <Flex align="baseline" gap="0.25rem">
            <Typography key={`resilience-score-${overallScore}`} variant="h1" className={"resilience-big-number"} data-testid="resilience-big-number">
              {isResilienceLoading ? "0" : formatScore(overallScore)}
            </Typography>
            <Typography variant="h3" className="resilience-max-score">
              /
            </Typography>
            <Typography variant="h3" className="resilience-max-score">
              {maxScore}
            </Typography>
          </Flex>
          {(isResilienceLoading || !hasScore) && (
            <Tag
              className="resilience-awaiting-data-tag"
              colour="yellow"
              size="small"
              text={
                <Flex align="center" gap="0.25rem">
                  <Icon iconName="tabler/loader" size="s" />
                  <Typography variant="paragraph-s">{t("resilienceScoreCard.awaitingData", "Awaiting data")}</Typography>
                </Flex>
              }
              dataTestId="resilience-awaiting-data-badge"
            />
          )}
          {isDataReady && riskLevel !== "medium" && (
            <Tag
              className="resilience-risk-tag"
              colour="yellow"
              size="small"
              text={
                <Flex align="center" gap="0.25rem">
                  <span className="resilience-risk-icon-wrapper">
                    <Icon iconName={riskConfig.icon} size="s" />
                  </span>
                  <Typography variant="paragraph-s">{t(riskConfig.key, riskConfig.fallback)}</Typography>
                </Flex>
              }
              dataTestId={`resilience-risk-badge-${riskLevel}`}
            />
          )}
        </Flex>
      </Flex>

      {isDataReady && resilienceScoreViewModel && (
        <div className="resilience-metrics">
          <Flex justify="space-between" align="center" className="resilience-metric-row">
            <Flex align="center" gap="0.375rem">
              <Typography variant="paragraph-m">{t("resilienceScoreCard.metrics.soilHealth", "Soil Health")}</Typography>
            </Flex>
            <Flex align="center" gap="0.25rem" className="resilience-metric-value">
              <Tooltip
                placement="top"
                title={t("resilienceScoreCard.tooltipChangeVsLastYear", {
                  change: formatChangePct(resilienceScoreViewModel.soilHealthChangePct),
                })}
              >
                <Icon iconName={TREND_ICON[resilienceScoreViewModel.soilHealthTrend]} size="s" className={"resilience-metric-icon"} />
              </Tooltip>
              <Typography key={`soil-health-score-${resilienceScoreViewModel.soilHealthScore}`} variant="paragraph-m">
                {formatScore(resilienceScoreViewModel.soilHealthScore)}
              </Typography>
            </Flex>
          </Flex>

          <Flex justify="space-between" align="center" className="resilience-metric-row">
            <Flex align="center" gap="0.375rem">
              <Typography variant="paragraph-m">{t("resilienceScoreCard.metrics.waterManagement", "Water Management")}</Typography>
            </Flex>
            <Flex align="center" gap="0.25rem" className="resilience-metric-value">
              <Tooltip
                placement="top"
                title={t("resilienceScoreCard.tooltipChangeVsLastYear", {
                  change: formatChangePct(resilienceScoreViewModel.waterManagementChangePct),
                })}
              >
                <Icon iconName={TREND_ICON[resilienceScoreViewModel.waterManagementTrend]} size="s" className={"resilience-metric-icon"} />
              </Tooltip>
              <Typography key={`water-management-score-${resilienceScoreViewModel.waterManagementScore}`} variant="paragraph-m">
                {formatScore(resilienceScoreViewModel.waterManagementScore)}
              </Typography>
            </Flex>
          </Flex>

          <Flex justify="space-between" align="center" className="resilience-metric-row">
            <Flex align="center" gap="0.375rem">
              <Typography variant="paragraph-m">{t("resilienceScoreCard.metrics.biodiversity", "Biodiversity")}</Typography>
            </Flex>
            <Flex align="center" gap="0.25rem" className="resilience-metric-value">
              <Tooltip
                placement="top"
                title={t("resilienceScoreCard.tooltipChangeVsLastYear", {
                  change: formatChangePct(resilienceScoreViewModel.biodiversityChangePct),
                })}
              >
                <Icon iconName={TREND_ICON[resilienceScoreViewModel.biodiversityTrend]} size="s" className={"resilience-metric-icon"} />
              </Tooltip>
              <Typography key={`biodiversity-score-${resilienceScoreViewModel.biodiversityScore}`} variant="paragraph-m">
                {formatScore(resilienceScoreViewModel.biodiversityScore)}
              </Typography>
            </Flex>
          </Flex>
        </div>
      )}

      <ErrorBoundary>
        <ResilienceScoreContextDrawer
          visible={showContextDrawer}
          onClose={() => setShowContextDrawer(false)}
          selectedYear={selectedYear}
          farmResilienceScore={farmResilienceScore}
        />
      </ErrorBoundary>
    </div>
  );
};

export default ResilienceScoreCard;
